// NPM packages
import React, { useState } from 'react';
import { Card, Modal, Box, Backdrop, Button } from '@mui/material';
import { Typography } from '@mui/material';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';

// All other imports
import { VideoResourceStoryblok } from 'types/storyblokTypes';
import { storyblokEditable } from '@storyblok/react';
import {
  IMAGE_COLUMN_WIDTH_PX,
  RESOURCE_COMPONENT_COLUMN_GAP as COLUMN_GAP,
} from '../utils';
import ResourceInformation from '../ResourceInformation';

// All other imports

const Root = styled.div(({ theme }) => {
  return {
    minWidth: theme.maxToolWidth,
    marginTop: '1.75rem',
    display: 'grid',
    gridTemplateColumns: `${IMAGE_COLUMN_WIDTH_PX}px 55rem`,
    gap: `${COLUMN_GAP}rem`,
  };
});

const VideoContentCard = styled(Card)({
  height: `${IMAGE_COLUMN_WIDTH_PX * 0.565574}px`,
  width: `${IMAGE_COLUMN_WIDTH_PX}px`,
});

const Video = styled.iframe({
  height: `${IMAGE_COLUMN_WIDTH_PX * 0.565574}px`,
  width: `${IMAGE_COLUMN_WIDTH_PX}px`,
});

const VideoOnlyContentCard = styled(Card)({
  width: '100%',
  padding: 0,
  '& .MuiCard-root': {
    padding: 0,
  },
});

const VideoContainer = styled.div({
  position: 'relative',
  width: '100%',
});

const ExpandButton = styled(Button)({
  position: 'absolute',
  top: '12px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: '#1976d2',
  color: 'white',
  minWidth: 'auto',
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: 'bold',
  zIndex: 1,
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
  border: '2px solid white',
  borderRadius: '8px',
  textTransform: 'uppercase',
  '&:hover': {
    backgroundColor: '#1565c0',
    transform: 'translateX(-50%) scale(1.05)',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
  },
});

const VideoFullWidth = styled.iframe({
  width: '100%',
  aspectRatio: '16/9',
  height: 'auto',
  display: 'block',
  border: 'none',
});

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 1,
  borderRadius: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '90vw',
  height: '90vh',
  overflow: 'hidden',
};

export interface VideoResourceProps {
  blok: VideoResourceStoryblok;
  className?: string;
}

function VideoResource(props: VideoResourceProps): React.ReactElement | null {
  const { blok, className } = props;
  const thumbnailExists = Boolean(blok.videoUrl);
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => setOpen(false);

  return (
    // letting video resources with no description be larger (used on quick guides)
    blok.description === ""
      ? 
      <>
        <Typography>{blok.title}</Typography>
        <VideoOnlyContentCard variant="elevation" raised>
          <VideoContainer>
            <ExpandButton onClick={handleOpen} variant="contained">
              Expand
            </ExpandButton>
            <VideoFullWidth
              src={blok.videoUrl}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </VideoContainer>
        </VideoOnlyContentCard>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="video-modal"
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{ 
            backdrop: { 
              timeout: 500,
              onClick: handleClose 
            } 
          }}
        >
          <Box sx={modalStyle}>
            <iframe
              src={blok.videoUrl}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </Box>
        </Modal>
      </>
      : 
    <Root className={className} {...storyblokEditable(blok)}>
      <VideoContentCard variant="elevation" raised>
        {thumbnailExists ? (
          <Video
            src={blok.videoUrl}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            frameBorder="0"
            allowFullScreen
          />
        ) : (
          <div></div>
        )}
      </VideoContentCard>
      <ResourceInformation blok={blok} />
    </Root>
  );
}

export default observer(VideoResource);
